import React from 'react';
import { Grid, Typography, Skeleton, Box } from '@mui/material';
import ButtonComponent from 'src/components/ButtonComponent';
import { fixedDigitsFormat } from 'src/utils/number';
import { CardWrapper, WelcomeCard } from 'src/pages/Welcome/style';
import { useGetAvailableBudgetQuery } from 'src/redux/services/credits';
import { useNavigate } from 'react-router-dom';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import Column from 'src/frontend-common/components/Column';
import Row from 'src/frontend-common/components/Row';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import useFetchStatus from 'src/hooks/useFetchStatus';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';
import { CheckCircle, Error } from '@mui/icons-material';
import { colors } from 'src/frontend-common/utils/palette';

export interface Props {
  sumOfCost: number | null;
}

const FirstThreeCards: React.FC<Props> = ({ sumOfCost }) => {
  const navigate = useNavigate();

  const { data: availableBudget, isFetching: isFetchingAvailableBudget } = useGetAvailableBudgetQuery();

  const AddFundsHandler = () => {
    navigate('/settings/?tab=billing');
  };

  const { status, statusIsFetching, statusIsError } = useFetchStatus();

  return (
    <CardWrapper container>
      <Grid item xs={12} md={4}>
        <WelcomeCard>
          <Column style={{ height: '100%' }}>
            <Typography variant="h4">Total Cost</Typography>
            {sumOfCost === null ? (
              <Skeleton variant="rectangular" width={160} height={29} style={{ marginTop: '16px' }} data-testid="total-cost-skeleton" />
            ) : (
              <Typography variant="h3" data-testid="total-cost-value">{`${useGetCurrency()} ${fixedDigitsFormat(sumOfCost, 2)}`}</Typography>
            )}
          </Column>
        </WelcomeCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <WelcomeCard>
          <Box>
            <Typography variant="h4">Balance</Typography>
            {isFetchingAvailableBudget && <Skeleton variant="rectangular" width={160} height={29} style={{ marginTop: '16px' }} />}
            {!isFetchingAvailableBudget && availableBudget && (
              <Typography variant="h3">{`${useGetCurrency()} ${fixedDigitsFormat(availableBudget['total'], 2)}`}</Typography>
            )}
          </Box>
          <Box>
            <ButtonComponent text="Add funds" buttontype="mainaction" onClick={AddFundsHandler} />
          </Box>
        </WelcomeCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <WelcomeCard>
          <Column>
            <>
              <Row hAlign="space-between">
                <Typography variant="h4">Platform Status</Typography>
                <CheckCircle data-testid="success-icon" sx={{ color: colors.success.main }} />
              </Row>
              {statusIsFetching && <Skeleton variant="rectangular" height={125} width="100%" />}
              {statusIsError && (
                <>
                  <FailureScreen minHeigth={50} align="flex-start" padding={0}>
                    An error occurred while fetching the platform status. Please retray later
                  </FailureScreen>
                  <Error data-testid="error-icon" sx={{ color: colors.error.main }} />;
                </>
              )}
              {!statusIsError && status && <Typography variant="h3">All Systems Operational</Typography>}
            </>
          </Column>
          <Box>
            <a href="https://trustfull.statuspage.io/" target="_blank" rel="noreferrer">
              Go to status page <ArrowForwardIcon />
            </a>
          </Box>
        </WelcomeCard>
      </Grid>
    </CardWrapper>
  );
};

export default FirstThreeCards;
